import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { btnPrimary, btnSecondary, ConfirmDelete, FieldError, inputCls, Panel } from "@/components/admin/AdminLayout";

const iconBtn = "h-11 w-11 shrink-0 border border-zinc-700 bg-transparent p-0 text-zinc-200 hover:bg-zinc-800 disabled:opacity-30";

const ItemForm = ({ fields, initial, onSubmit, onCancel, submitLabel, prefix }) => {
  const [vals, setVals] = useState(initial);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    const req = fields[0];
    if (!vals[req.key]?.trim()) return setError(req.requiredMsg);
    setError("");
    const ok = await onSubmit(vals);
    if (ok && !onCancel) setVals(initial);
  };
  return (
    <form onSubmit={submit} noValidate className="space-y-2" data-testid={`${prefix}-form`}>
      {fields.map((f) =>
        f.area ? (
          <Textarea key={f.key} placeholder={f.label} value={vals[f.key]} rows={2} onChange={(e) => setVals({ ...vals, [f.key]: e.target.value })} className="bg-[#121214] border-zinc-700 text-base" data-testid={`${prefix}-${f.key}-input`} />
        ) : (
          <Input key={f.key} placeholder={f.label} value={vals[f.key]} onChange={(e) => setVals({ ...vals, [f.key]: e.target.value })} className={inputCls} data-testid={`${prefix}-${f.key}-input`} />
        )
      )}
      <FieldError msg={error} testId={`${prefix}-error`} />
      <div className="flex gap-2">
        <Button type="submit" className={btnPrimary} data-testid={`${prefix}-submit-button`}>
          {!onCancel && <Plus className="h-4 w-4" />} {submitLabel}
        </Button>
        {onCancel && <Button type="button" className={btnSecondary} onClick={onCancel} data-testid={`${prefix}-cancel-button`}>Cancel</Button>}
      </div>
    </form>
  );
};

export const ListEditor = ({ title, endpoint, fields, addLabel, testPrefix }) => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const blank = Object.fromEntries(fields.map((f) => [f.key, ""]));

  useEffect(() => {
    let alive = true;
    api.get(`/admin/${endpoint}`).then((r) => alive && setItems(r.data));
    return () => {
      alive = false;
    };
  }, [endpoint]);

  const run = async (fn, msg) => {
    try {
      await fn();
      if (msg) toast.success(msg);
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    }
  };
  const add = (vals) => run(async () => setItems([...items, (await api.post(`/admin/${endpoint}`, vals)).data]), "Added");
  const update = (id, vals) =>
    run(async () => {
      const { data } = await api.put(`/admin/${endpoint}/${id}`, vals);
      setItems(items.map((x) => (x.id === id ? data : x)));
      setEditing(null);
    }, "Saved");
  const remove = (id) => run(async () => {
    await api.delete(`/admin/${endpoint}/${id}`);
    setItems((cur) => cur.filter((x) => x.id !== id));
  }, "Deleted");
  const move = (i, d) => {
    const next = [...items];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    setItems(next);
    run(async () => setItems((await api.post(`/admin/${endpoint}/reorder`, { ids: next.map((x) => x.id) })).data));
  };

  return (
    <Panel title={title} testId={`${testPrefix}-panel`}>
      <ul className="space-y-2" data-testid={`${testPrefix}-list`}>
        {items.map((item, i) => (
          <li key={item.id} className="rounded-md border border-zinc-800 bg-[#121214] p-3" data-testid={`${testPrefix}-item-${i}`}>
            {editing === item.id ? (
              <ItemForm fields={fields} initial={{ ...blank, ...item }} submitLabel="Save" prefix={`${testPrefix}-edit`} onCancel={() => setEditing(null)} onSubmit={(v) => update(item.id, v)} />
            ) : (
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1 pt-1">
                  <p className="break-words text-base text-zinc-100" data-testid={`${testPrefix}-item-text-${i}`}>{item[fields[0].key]}</p>
                  {fields[1] && item[fields[1].key] && <p className="mt-1 break-words text-sm text-zinc-400">{item[fields[1].key]}</p>}
                </div>
                <div className="grid shrink-0 grid-cols-2 gap-1.5 sm:flex">
                  <Button type="button" className={iconBtn} disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up" data-testid={`${testPrefix}-move-up-${i}`}><ArrowUp className="h-4 w-4" /></Button>
                  <Button type="button" className={iconBtn} disabled={i === items.length - 1} onClick={() => move(i, 1)} aria-label="Move down" data-testid={`${testPrefix}-move-down-${i}`}><ArrowDown className="h-4 w-4" /></Button>
                  <Button type="button" className={iconBtn} onClick={() => setEditing(item.id)} aria-label="Edit" data-testid={`${testPrefix}-edit-${i}`}><Pencil className="h-4 w-4" /></Button>
                  <ConfirmDelete
                    description={`“${item[fields[0].key]}” will be removed from the website.`}
                    onConfirm={() => remove(item.id)}
                    trigger={<Button type="button" className={iconBtn} aria-label="Delete" data-testid={`${testPrefix}-delete-${i}`}><Trash2 className="h-4 w-4" /></Button>}
                  />
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
      <div className="mt-5 border-t border-zinc-800 pt-4">
        <ItemForm fields={fields} initial={blank} submitLabel={addLabel} prefix={`${testPrefix}-add`} onSubmit={add} />
      </div>
    </Panel>
  );
};
